import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useWhitelabel } from "../../../contexts/WhitelabelContext";
import NotyfContext from "../../../contexts/NotyfContext";
import {
  getMailConfiguration,
  updateMailConfiguration,
} from "../../../redux/slices/mailConfiguration";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Button,
  Form as BootstrapForm,
  Col,
  Row,
  Spinner,
  Collapse,
  Card,
  Alert,
} from "react-bootstrap";
import Loader from "../../../components/spinners/Loader";
import useWindowSize from "../../../hooks/useWindowSize";
import useDeviceType from "../../../hooks/useDeviceType";

const MailConfigurationSettings = ({ activeTab }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const [activeUser, setActiveUser] = useState({});
  const { backgroundColor, fontColor } = useWhitelabel();
  const notyf = useContext(NotyfContext);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const { mailConfiguration } = useSelector((state) => state.mailConfiguration);
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";

  const renderHealthStatus = (status) => {
    switch (status) {
      case "UP":
        return <div className="circle pulse green mb-3" />;
      case "DOWN":
        return <div className="circle pulse red mb-3" />;
      default:
        return <div className="blue mb-3" />;
    }
  };

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [impersonationUser, user]);

  useEffect(() => {
    const fetchMailConfiguration = async () => {
      setIsLoading(true);
      await dispatch(getMailConfiguration())
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            dismissible: false,
            ripple: true,
            position: {
              x: "center",
              y: "top",
            },
          });
        })
        .finally(() => {
          setTimeout(() => {
            setIsLoading(false);
          }, 500);
        });
    };

    if (activeUser.id && activeTab === "#MailConfig") {
      fetchMailConfiguration();
    }
  }, [activeTab, activeUser, dispatch, notyf]);

  const initialValues = {
    host: mailConfiguration?.host ?? "",
    port: mailConfiguration?.port ?? "",
    username: mailConfiguration?.username ?? "",
    changePassword: false,
    password: "",
    confirmPassword: "",
    useTls: mailConfiguration?.useTls ?? false,
    fromAddress: mailConfiguration?.fromAddress ?? "",
  };

  const validationSchema = Yup.object().shape({
    host: Yup.string().required("Host is required"),
    port: Yup.number().required("Port is required").positive().integer(),
    username: Yup.string().required("Username is required"),
    fromAddress: Yup.string()
      .email("Invalid email")
      .required("From Address is required"),
    password: Yup.string().when("changePassword", {
      is: true,
      then: (schema) => schema.required("Password is required"),
    }),
    confirmPassword: Yup.string().when("changePassword", {
      is: true,
      then: (schema) =>
        schema
          .oneOf([Yup.ref("password"), null], "Passwords must match")
          .required("Confirm Password is required"),
    }),
  });

  return (
    <Card>
      <Card.Header>
        <Card.Title>{t("settings.mailConfig.title")}</Card.Title>
      </Card.Header>
      <Card.Body>
        {isLoading ? (
          <Loader />
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={async (values, { setSubmitting }) => {
              const data = {
                host: values.host,
                port: values.port,
                username: values.username,
                password: values.password,
                fromAddress: values.fromAddress,
                useTls: values.useTls,
              };
              await dispatch(updateMailConfiguration({ data: data }))
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: t("settings.mailConfig.success"),
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error || t("settings.mailConfig.error"),
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
            }) => (
              <Form>
                {mailConfiguration && (
                  <Alert variant="info" className="mb-3 d-flex flex-column p-3">
                    <div className="mb-3">
                      <strong>{t("settings.mailConfig.healthStatus")}:</strong>{" "}
                    </div>
                    {renderHealthStatus(mailConfiguration.healthStatus)}
                    {mailConfiguration.lastCheckedAt && (
                      <div>
                        <strong>
                          {t("settings.mailConfig.lastCheckedAt")}:
                        </strong>{" "}
                        {mailConfiguration.lastCheckedAt}
                      </div>
                    )}
                  </Alert>
                )}
                <BootstrapForm.Group className="mb-3">
                  <BootstrapForm.Label>
                    {t("settings.mailConfig.host")}
                  </BootstrapForm.Label>
                  <BootstrapForm.Control
                    type="text"
                    name="host"
                    value={values.host}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.host && !!errors.host}
                  />
                  <BootstrapForm.Control.Feedback type="invalid">
                    {errors.host}
                  </BootstrapForm.Control.Feedback>
                </BootstrapForm.Group>
                <BootstrapForm.Group className="mb-3">
                  <BootstrapForm.Label>
                    {t("settings.mailConfig.port")}
                  </BootstrapForm.Label>
                  <BootstrapForm.Control
                    type="number"
                    name="port"
                    value={values.port}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.port && !!errors.port}
                  />
                  <BootstrapForm.Control.Feedback type="invalid">
                    {errors.port}
                  </BootstrapForm.Control.Feedback>
                </BootstrapForm.Group>
                <BootstrapForm.Group className="mb-3">
                  <BootstrapForm.Label>
                    {t("settings.mailConfig.fromAddress")}
                  </BootstrapForm.Label>
                  <BootstrapForm.Control
                    type="email"
                    name="fromAddress"
                    value={values.fromAddress}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.fromAddress && !!errors.fromAddress}
                  />
                  <BootstrapForm.Control.Feedback type="invalid">
                    {errors.fromAddress}
                  </BootstrapForm.Control.Feedback>
                </BootstrapForm.Group>
                <BootstrapForm.Group className="mb-3">
                  <BootstrapForm.Label>
                    {t("settings.mailConfig.username")}
                  </BootstrapForm.Label>
                  <BootstrapForm.Control
                    type="text"
                    name="username"
                    value={values.username}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.username && !!errors.username}
                  />
                  <BootstrapForm.Control.Feedback type="invalid">
                    {errors.username}
                  </BootstrapForm.Control.Feedback>
                </BootstrapForm.Group>
                <BootstrapForm.Group className="mb-3">
                  <BootstrapForm.Check
                    type="checkbox"
                    label={t("settings.mailConfig.changePassword")}
                    name="changePassword"
                    checked={values.changePassword}
                    onChange={handleChange}
                    style={{
                      "--dynamic-bg-color": backgroundColor,
                      "--dynamic-font-color": fontColor,
                    }}
                  />
                </BootstrapForm.Group>
                <Collapse in={values.changePassword} dimension="height">
                  <div>
                    <BootstrapForm.Group className="mb-3">
                      <BootstrapForm.Label>
                        {t("settings.mailConfig.password")}
                      </BootstrapForm.Label>
                      <BootstrapForm.Control
                        type="password"
                        name="password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.password && !!errors.password}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.password}
                      </BootstrapForm.Control.Feedback>
                    </BootstrapForm.Group>
                    <BootstrapForm.Group className="mb-3">
                      <BootstrapForm.Label>
                        {t("settings.mailConfig.confirmPassword")}
                      </BootstrapForm.Label>
                      <BootstrapForm.Control
                        type="password"
                        name="confirmPassword"
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.confirmPassword && !!errors.confirmPassword
                        }
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.confirmPassword}
                      </BootstrapForm.Control.Feedback>
                    </BootstrapForm.Group>
                  </div>
                </Collapse>

                <BootstrapForm.Group className="mb-3">
                  <BootstrapForm.Check
                    type="switch"
                    id="useTlsSwitch"
                    label={t("settings.mailConfig.useTLS")}
                    name="useTls"
                    checked={values.useTls}
                    onChange={handleChange}
                    style={{
                      "--dynamic-bg-color": backgroundColor,
                      "--dynamic-font-color": fontColor,
                    }}
                  />
                </BootstrapForm.Group>

                {isDesktop && (
                  <div className="large-screen-form-footer">
                    <Row>
                      <Col md="auto" className="ms-auto">
                        <Button
                          type="submit"
                          variant="primary"
                          style={{
                            "--dynamic-bg-color": backgroundColor,
                            "--dynamic-font-color": fontColor,
                            border: "none",
                          }}
                        >
                          {t("settings.mailConfig.save")}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                )}
                {!isDesktop && (
                  <div className="mt-3 d-flex justify-content-center">
                    <Button
                      type="submit"
                      variant="primary"
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                        padding: "10px",
                        width: "100%",
                      }}
                    >
                      {t("settings.mailConfig.save")}
                    </Button>
                  </div>
                )}
              </Form>
            )}
          </Formik>
        )}
      </Card.Body>
    </Card>
  );
};

export default MailConfigurationSettings;
